import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { EExportDialogComponent, EExportDialogLabels, EExportFormatOption } from '@shared/components/dialogs/e-export-dialog/e-export-dialog.component';
import { NOTIFIER } from '@shared/notifier/types';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdMomentsExportOutcome, HdMomentsExportService } from './hd-moments-export.service';

/** Wat er geëxporteerd wordt: alle momenten van een chart, of één chart als moment. */
export type HdMomentsExportTarget =
  | { readonly kind: 'chart'; readonly chartId: string; readonly chartName: string }
  | { readonly kind: 'moment'; readonly chartId: string };

/** De bullets in de dialoog; de teksten staan onder humanDesign.momentsExport.md. */
const INCLUDES_ALL = ['meta', 'details', 'context', 'notes', 'planets', 'cycles'] as const;
const INCLUDES_ONE = ['meta', 'details', 'context', 'notes', 'planets'] as const;
const EXCLUDES = ['chart', 'iching', 'reimport'] as const;

/**
 * Exportdialoog voor momenten: één formaat (Markdown), met de uitleg wat erin zit en wat niet.
 * Gedeeld door de Momenten-sectie, de Alle momenten-dialoog, de tijdlijnpagina en het
 * Notities-paneel; de host zet enkel het doel.
 */
@Component({
  selector: 'hd-moments-export-dialog',
  imports: [EExportDialogComponent],
  template: `
    <e-export-dialog
      [open]="target() !== null"
      (openChange)="onOpenChange($event)"
      [title]="text().title"
      [intro]="text().intro"
      [options]="options()"
      [labels]="labels()"
      [busy]="busy()"
      (exportRequested)="run()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentsExportDialogComponent {
  readonly target = model<HdMomentsExportTarget | null>(null);

  private readonly exporter = inject(HdMomentsExportService);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly busy = signal(false);

  private readonly translate = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return (key: string, params?: Record<string, unknown>): string => this.transloco.translate<string>(key, params, lang);
  });

  protected readonly text = computed(() => {
    const t = this.translate();
    const single = this.target()?.kind === 'moment';
    return {
      title: t(single ? 'humanDesign.momentsExport.dialogTitleMoment' : 'humanDesign.momentsExport.dialogTitleChart'),
      intro: t('humanDesign.momentsExport.intro'),
    };
  });

  protected readonly labels = computed<EExportDialogLabels>(() => {
    const t = this.translate();
    return {
      includes: t('humanDesign.momentsExport.includes'),
      excludes: t('humanDesign.momentsExport.excludes'),
      download: t('humanDesign.momentsExport.download'),
    };
  });

  protected readonly options = computed<readonly EExportFormatOption[]>(() => {
    const t = this.translate();
    const single = this.target()?.kind === 'moment';
    return [
      {
        id: 'markdown',
        icon: 'file-lines',
        title: t('humanDesign.momentsExport.md.title'),
        description: t(single ? 'humanDesign.momentsExport.md.descriptionMoment' : 'humanDesign.momentsExport.md.descriptionChart'),
        includes: (single ? INCLUDES_ONE : INCLUDES_ALL).map(key => t(`humanDesign.momentsExport.md.includes.${key}`)),
        excludes: EXCLUDES.map(key => t(`humanDesign.momentsExport.md.excludes.${key}`)),
        note: t('humanDesign.momentsExport.md.note'),
      },
    ];
  });

  protected onOpenChange(open: boolean): void {
    if (!open && !this.busy()) this.target.set(null);
  }

  protected async run(): Promise<void> {
    const target = this.target();
    if (!target || this.busy()) return;
    const t = this.translate();
    this.busy.set(true);
    let outcome: HdMomentsExportOutcome;
    try {
      outcome = target.kind === 'chart'
        ? await this.exporter.exportChartMoments(target.chartId, target.chartName)
        : await this.exporter.exportMoment(target.chartId);
    } catch {
      // De dialoog blijft open: opnieuw proberen kan meteen.
      this.notifier.error(t('humanDesign.momentsExport.error'));
      return;
    } finally {
      this.busy.set(false);
    }
    this.target.set(null);
    if (outcome.count === 0) {
      this.notifier.info(t('humanDesign.momentsExport.empty'));
      return;
    }
    if (outcome.planetsMissing) this.notifier.warning(t('humanDesign.momentsExport.planetsMissing'));
    if (outcome.attachmentsMissing > 0) this.notifier.warning(t('humanDesign.momentsExport.attachmentsMissing', { count: outcome.attachmentsMissing }));
  }
}
